import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Input, Select, Textarea } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { allocateChecked, allocationError, PAYMENT_METHOD_LABEL, recordReceipt } from "@/services/paymentService";
import FeeChecklist from "@/components/feature/FeeChecklist";
import { buildProvisionalReceipt, toFeeSnapshot, type ReceiptData } from "@/utils/receipt";
import { formatMoney, todayISO } from "@/utils/format";
import type { EnrollmentBalance, FeeLine, PaymentMethod, Receipt } from "@/types";
import { currencySymbol } from "@/utils/currency";

/** Option de sélection : une inscription avec sa situation. */
export interface EnrollmentOption {
  id: string;
  studentId: string;
  studentName: string;
  matricule: string;
  className: string;
  cycleLabel: string;
  schoolYear: string;
  lines: FeeLine[];
}

const METHODS = Object.keys(PAYMENT_METHOD_LABEL) as PaymentMethod[];

interface Props {
  open: boolean;
  onClose: () => void;
  options: EnrollmentOption[];
  presetEnrollmentId?: string;
  onRecorded: (receipt: Receipt, balance: EnrollmentBalance) => void;
  /** Paiement noté hors ligne : reçu provisoire à imprimer. */
  onProvisional: (data: ReceiptData) => void;
}

/**
 * Encaissement au guichet : la comptable coche les frais que le versement règle et saisit le montant reçu.
 * Il va d'abord sur les frais cochés ; un surplus va sur la suite de la fiche ; un montant supérieur à tout
 * le reste à payer est refusé. Un seul reçu numéroté est émis pour l'ensemble.
 */
export default function EncaissementModal({ open, onClose, options, presetEnrollmentId, onRecorded, onProvisional }: Props) {
  const { notify } = useToast();
  const [busy, setBusy] = useState(false);
  const [enrollmentId, setEnrollmentId] = useState("");
  const [received, setReceived] = useState("");
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [date, setDate] = useState(todayISO());
  const [method, setMethod] = useState<PaymentMethod>("ESPECES");
  const [note, setNote] = useState("");

  const current = options.find((o) => o.id === enrollmentId) ?? null;
  const payable = useMemo(() => (current?.lines ?? []).filter((l) => l.remaining > 0), [current]);
  const amount = Number(received) || 0;
  const allocation = useMemo(() => allocateChecked(payable, amount, checked), [payable, amount, checked]);
  const error = allocationError(allocation, amount, checked.size);
  const toggle = (key: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  useEffect(() => {
    if (!open) return;
    const firstId = presetEnrollmentId ?? options[0]?.id ?? "";
    setEnrollmentId(firstId);
    setReceived("");
    setChecked(new Set());
    setDate(todayISO());
    setMethod("ESPECES");
    setNote("");
  }, [open, presetEnrollmentId, options]);

  const changeEnrollment = (id: string) => {
    setEnrollmentId(id);
    setReceived("");
    setChecked(new Set());
  };

  const lineTotal = Object.values(allocation.amounts).reduce((s, v) => s + v, 0);

  const handleSubmit = async () => {
    if (!current) {
      notify("Choisissez d'abord une inscription.", "error");
      return;
    }
    if (error) {
      notify(error, "error");
      return;
    }
    if (lineTotal <= 0) {
      notify("Cochez les frais réglés et saisissez le montant reçu.", "error");
      return;
    }
    setBusy(true);
    try {
      const recorded = await recordReceipt(
        current.id,
        {
          date,
          method,
          note,
          lines: payable.map((l) => ({ feeKey: l.key, amount: allocation.amounts[l.key] ?? 0 })).filter((l) => l.amount > 0),
        },
        {
          label: `Paiement — ${current.studentName} (${current.matricule}), ${formatMoney(lineTotal)}`,
          provisional: (ref) =>
            buildProvisionalReceipt({
              ref,
              date,
              method,
              note,
              student: {
                name: current.studentName,
                matricule: current.matricule,
                className: current.className,
                cycleLabel: current.cycleLabel,
                schoolYear: current.schoolYear,
              },
              fees: current.lines.map(toFeeSnapshot),
              amounts: allocation.amounts,
            }),
        },
      );
      if ("queued" in recorded) {
        // Hors ligne : noté sur l'appareil, avec un reçu provisoire ; le reçu officiel viendra du serveur.
        notify("Hors ligne : paiement noté sur l'appareil. Remettez le reçu provisoire ; il sera envoyé au retour de la connexion.");
        if (recorded.provisional) onProvisional(recorded.provisional);
      } else {
        notify(`Versement enregistré — reçu ${recorded.receipt.receiptNo}.`);
        onRecorded(recorded.receipt, recorded.balance);
      }
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Erreur lors de l'encaissement.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nouvel encaissement"
      subtitle="Cochez les frais réglés, puis saisissez le montant reçu"
      icon="ri-hand-coin-line"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-save-line" disabled={busy || !current || lineTotal <= 0 || !!error} onClick={handleSubmit}>
            {busy ? "Enregistrement..." : `Encaisser ${lineTotal > 0 ? formatMoney(lineTotal) : ""} & imprimer`}
          </Button>
        </>
      }
    >
      {options.length === 0 ? (
        <p className="rounded-md border border-dashed border-background-300 py-10 text-center text-sm text-foreground-400">
          Aucune inscription avec un reste à payer pour les filtres actuels.
        </p>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Inscription (élève)"
              required
              value={enrollmentId}
              disabled={!!presetEnrollmentId}
              onChange={(e) => changeEnrollment(e.target.value)}
            >
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.studentName} — {o.className} ({o.schoolYear})
                </option>
              ))}
            </Select>
            <Input label="Matricule" value={current?.matricule ?? ""} readOnly />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input
              label={`Montant reçu (${currencySymbol()})`}
              type="number"
              min={0}
              value={received}
              onChange={(e) => setReceived(e.target.value)}
              hint={`Reste à payer : ${formatMoney(allocation.totalRemaining)}`}
            />
            <Input label="Date du versement" type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} />
            <Select label="Mode de paiement" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
              {METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABEL[m]}
                </option>
              ))}
            </Select>
          </div>

          <FeeChecklist lines={payable} checked={checked} onToggle={toggle} allocation={allocation} amount={amount} />
          {error && (
            <p role="alert" className="animate-fade-in rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-sm text-accent-800">
              <i className="ri-error-warning-line mr-1.5"></i>
              {error}
            </p>
          )}

          <Textarea
            label="Note (optionnel)"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ex. versé par le père, référence du chèque..."
          />
        </div>
      )}
    </Modal>
  );
}
